import { useState } from "react";
import { toast } from "sonner";
import { Plus, FileImage, Check, X, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { REIMB_STATUS, EMAIL_STATUS, fmtDate, fmtCHF, fmtDateTime, todayZurich } from "@/lib/format";
import { MapBadge, StatusBadge } from "@/components/StatusBadge";
import { Card, Empty, Field, PageHeader, Sel } from "@/components/Field";
import { FormDialog, useSubmit } from "@/components/dialogs/FormDialog";

const MAIL = { ...EMAIL_STATUS, no_recipient: { label: "Responsabile rimborsi non impostato", tone: "warning" } };

const NewDialog = ({ open, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ description: "", amount: "", expense_date: todayZurich(), iban: "", event_id: "" });
  const [file, setFile] = useState(null);
  const events = useFetch(open ? "/events" : null).data || [];
  const { saving, run } = useSubmit();
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const submit = () => run(async () => {
    if (!file) throw Object.assign(new Error(), { response: { data: { detail: "Carica la foto o il PDF dello scontrino" } } });
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => v && fd.append(k, v));
    fd.append("file", file);
    const { data } = await api.post("/reimbursements", fd);
    if (data.email_status === "no_recipient") toast.warning("Richiesta salvata, ma il responsabile rimborsi non è impostato: nessuna e-mail inviata");
    onOpenChange(false);
    setFile(null);
    setF({ description: "", amount: "", expense_date: todayZurich(), iban: "", event_id: "" });
    onSaved();
  }, "Richiesta di rimborso inviata");
  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title="Richiesta di rimborso spese" onSubmit={submit} saving={saving} testId="reimb-dialog" submitLabel="Invia richiesta">
      <Field label="Scontrino *" hint="Foto (JPG, PNG) o PDF, max 10 MB">
        <Input data-testid="reimb-file-input" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-12 pt-2.5" />
      </Field>
      <Field label="Cosa hai comprato *"><Input data-testid="reimb-description-input" required minLength={3} value={f.description} onChange={set("description")} className="h-11" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Importo (CHF) *"><Input data-testid="reimb-amount-input" type="number" min="0.05" step="0.05" required value={f.amount} onChange={set("amount")} className="h-11" /></Field>
        <Field label="Data della spesa *"><Input data-testid="reimb-date-input" type="date" required value={f.expense_date} onChange={set("expense_date")} className="h-11" /></Field>
      </div>
      <Field label="IBAN per il rimborso" hint="Facoltativo"><Input data-testid="reimb-iban-input" value={f.iban} onChange={set("iban")} className="h-11" /></Field>
      {events.length > 0 && <Field label="Evento collegato"><Sel testId="reimb-event-select" value={f.event_id} onChange={set("event_id")} placeholder="Nessuno" options={events.map((e) => ({ value: e.id, label: e.name }))} /></Field>}
    </FormDialog>
  );
};

export default function ReimbursementsPage() {
  const { can } = useAuth();
  const approver = can("reimbursements", "approve");
  const { data, reload } = useFetch("/reimbursements");
  const [open, setOpen] = useState(false);
  const receipt = async (r) => {
    const win = window.open("", "_blank");
    try {
      const res = await api.get(`/reimbursements/${r.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (win) win.location.href = url;
      else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { win?.close(); toast.error(formatError(e)); }
  };
  const setStatus = async (r, status) => {
    const note = status === "rejected" ? window.prompt("Motivo (facoltativo)") : null;
    if (status === "rejected" && note === null) return;
    const fd = new FormData();
    fd.append("status", status);
    if (note) fd.append("note", note);
    try { await api.post(`/reimbursements/${r.id}/status`, fd); toast.success("Stato aggiornato"); reload(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <>
      <PageHeader title="Rimborsi spese" subtitle={approver ? "Tutte le richieste dell'associazione" : "Le tue richieste di rimborso"}>
        {can("reimbursements", "create") && <Button data-testid="reimb-new-button" className="h-11" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> Nuova richiesta</Button>}
      </PageHeader>
      {data && data.length === 0 ? <Empty testId="reimb-empty" text="Nessuna richiesta di rimborso." /> : (
        <Card><ul className="divide-y">
          {data?.map((r) => (
            <li key={r.id} data-testid={`reimb-row-${r.id}`} className="px-5 py-4 space-y-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-base font-semibold text-slate-900">{r.description} · {fmtCHF(r.amount)}</div>
                  <div className="text-sm text-slate-500">{r.created_by_name} · spesa del {fmtDate(r.expense_date)} · inviata {fmtDateTime(r.created_at)}{r.event_name && ` · ${r.event_name}`}{r.iban && ` · IBAN ${r.iban}`}</div>
                  {r.status_note && <div className="text-sm text-slate-600">Nota: {r.status_note}</div>}
                </div>
                <div className="flex flex-wrap gap-1.5"><MapBadge map={REIMB_STATUS} value={r.status} testId={`reimb-status-${r.id}`} />{approver && <StatusBadge {...(MAIL[r.email_status] || { label: r.email_status })} />}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid={`reimb-receipt-${r.id}`} variant="outline" className="h-10" onClick={() => receipt(r)}><FileImage className="h-4 w-4 mr-1" /> Scontrino</Button>
                {approver && r.status === "submitted" && <Button data-testid={`reimb-approve-${r.id}`} className="h-10" onClick={() => setStatus(r, "approved")}><Check className="h-4 w-4 mr-1" /> Approva</Button>}
                {approver && r.status === "submitted" && <Button data-testid={`reimb-reject-${r.id}`} variant="ghost" className="h-10 text-red-700" onClick={() => setStatus(r, "rejected")}><X className="h-4 w-4 mr-1" /> Rifiuta</Button>}
                {approver && r.status === "approved" && <Button data-testid={`reimb-paid-${r.id}`} className="h-10 bg-green-700 hover:bg-green-800" onClick={() => setStatus(r, "paid")}><BadgeCheck className="h-4 w-4 mr-1" /> Rimborsato</Button>}
              </div>
            </li>
          ))}
        </ul></Card>
      )}
      <NewDialog open={open} onOpenChange={setOpen} onSaved={reload} />
    </>
  );
}
